import type { Lang, Word } from '../types'
import { isFillerWord } from './filler'

/**
 * Phiên âm bằng Whisper chạy 100% trong trình duyệt (transformers.js, WASM).
 * Thư viện được nạp động từ CDN để giữ bundle nhẹ và build nhanh.
 * Lần chạy đầu sẽ tải model (~40MB, whisper-tiny) — cần internet, sau đó được cache.
 *
 * Đây là hướng "local-first": không upload file lên bất kỳ server nào.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let transcriberPromise: Promise<any> | null = null

async function getTranscriber(onStatus?: (s: string) => void) {
  if (!transcriberPromise) {
    transcriberPromise = (async () => {
      onStatus?.('Đang tải thư viện AI…')
      // Specifier động (biến) → TS trả về any, không cố resolve module URL.
      const cdnUrl = 'https://cdn.jsdelivr.net/npm/@xenova/transformers@2.17.2'
      const mod = await import(/* @vite-ignore */ cdnUrl)
      const { pipeline, env } = mod
      // Cho phép tải model từ Hugging Face Hub.
      env.allowLocalModels = false
      onStatus?.('Đang tải model Whisper (lần đầu ~40MB)…')
      return pipeline('automatic-speech-recognition', 'Xenova/whisper-tiny', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        progress_callback: (p: any) => {
          if (p?.status === 'progress' && p?.file) {
            onStatus?.(`Tải model: ${p.file} ${Math.round(p.progress || 0)}%`)
          }
        },
      })
    })()
  }
  return transcriberPromise
}

let counter = 0
const uid = () => `w${Date.now().toString(36)}_${counter++}`

export interface TranscribeResult {
  words: Word[]
  fullText: string
}

export async function transcribe(
  audio: Float32Array,
  lang: Lang,
  onStatus?: (s: string) => void,
): Promise<TranscribeResult> {
  const transcriber = await getTranscriber(onStatus)
  onStatus?.('Đang phiên âm…')

  const output = await transcriber(audio, {
    return_timestamps: 'word',
    chunk_length_s: 30,
    stride_length_s: 5,
    language: lang === 'auto' ? undefined : lang,
    task: 'transcribe',
  })

  const chunks: Array<{ text: string; timestamp: [number, number | null] }> =
    output?.chunks ?? []

  const words: Word[] = []
  for (let i = 0; i < chunks.length; i++) {
    const c = chunks[i]
    const text = (c.text ?? '').trim()
    if (!text) continue
    const start = c.timestamp?.[0] ?? 0
    // một số từ thiếu mốc kết thúc → dùng mốc bắt đầu của từ kế tiếp
    const nextStart = chunks[i + 1]?.timestamp?.[0]
    const end = c.timestamp?.[1] ?? nextStart ?? start + 0.3
    words.push({
      id: uid(),
      start,
      end: Math.max(end, start + 0.05),
      text,
      removed: false,
      isFiller: isFillerWord(text),
    })
  }

  return { words, fullText: (output?.text ?? '').trim() }
}
